import Text from 'components/Text/Text';
import React, { useContext, useMemo, useRef } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { useTranslation } from 'react-i18next';
import {
	DishAssignmentContext,
	IDishAssignmentContext,
} from '../../../../../dishAssignmentState/dishAssignmentContext';
import { UserRoles, IGetCustomersShort, EntityStatus } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { getCustomersShortInfo } from 'backend/customers';
import SelectDropdownReloader from 'components/SelectDropdownReloader/SelectDropdownReloader';
import { Select } from 'antd';
import style from './CustomerSelect.module.scss';

const { Option } = Select;

export const CustomerSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { setIDSelectedCustomer, IDSelectedCustomer } = useContext<IDishAssignmentContext>(DishAssignmentContext);
	const parentNodeRef = useRef<null | HTMLSpanElement>(null);
	const hasAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(
		hasAccess ? getCustomersShortInfo : null,
	);
	const { t } = useTranslation('listing');

	const customers = useMemo(() => {
		if (hasAccess) {
			return response?.data.customers.filter((customer) => customer.status === EntityStatus.Active);
		}
		return loggedUser.customers?.filter((customer) => customer.status === EntityStatus.Active) || [];
	}, [hasAccess, response, loggedUser]);

	return (
		<div className={style.container} data-test="CustomerSelect">
			<Text translationNameSpace="listing" translationKey="customerLabel" theme="neutralLight" size="xs" />
			<span ref={parentNodeRef} style={{ position: 'relative' }} />
			<Select
				className={style.select}
				onChange={(value) => setIDSelectedCustomer(value ? value : null)}
				value={IDSelectedCustomer}
				loading={loading}
				disabled={loading}
				showArrow={true}
				allowClear={true}
				notFoundContent={responseError ? <SelectDropdownReloader onReload={refetch} /> : undefined}
				showSearch={true}
				filterOption={(inputValue, option) =>
					option?.children ? String(option.children).toLowerCase().includes(inputValue.toLowerCase()) : false
				}
				listItemHeight={customers?.length}
				getPopupContainer={() => parentNodeRef.current as HTMLSpanElement}
				placeholder={t('selectPlaceholder')}>
				{customers?.length ? (
					<>
						<Option className={style.disabledItem} value="empty" disabled={true}>
							<Text
								translationNameSpace="listing"
								translationKey="selectAnyItemPlaceholder"
								asFragment={true}
							/>
						</Option>
						{customers.map((el) => (
							<Option value={el?.id} key={el?.id}>
								{el?.name}
							</Option>
						))}
					</>
				) : null}
			</Select>
		</div>
	);
};
